import { Adaptation } from '../../types';

export const calculateAdaptationStats = (adaptations: Adaptation[]) => {
  const total = adaptations.length;
  const categories = new Set(adaptations.map(a => a.category)).size;
  const avgParameters = adaptations.length > 0 
    ? adaptations.reduce((sum, a) => sum + a.scientificParameters.length, 0) / adaptations.length 
    : 0;
  
  return [
    { title: 'Total Adaptations', value: total },
    { title: 'Categories', value: categories },
    { title: 'Avg Parameters', value: avgParameters, precision: 1 },
    { title: 'Evidence Base', value: 92, suffix: '%' },
  ];
};

export const getCategoryProgress = (adaptations: Adaptation[], categories: string[]) => {
  return categories.map(category => {
    const count = adaptations.filter(a => a.category === category).length;
    return {
      title: category,
      percent: adaptations.length > 0 ? (count / adaptations.length) * 100 : 0,
      status: count > 0 ? 'success' as const : 'exception' as const,
    };
  });
};

export const getCategoryColor = (category: string): string => {
  const colorMap: { [key: string]: string } = {
    'Strength': 'red',
    'Hypertrophy': 'orange',
    'Endurance': 'green',
    'Power': 'purple',
    'Flexibility': 'blue',
    'Balance': 'cyan',
    'Coordination': 'magenta',
    'Mobility': 'lime',
  };
  return colorMap[category] || 'default';
};

export const validateAdaptation = (values: Partial<Adaptation>): string[] => {
  const errors: string[] = [];
  
  if (!values.name || values.name.trim().length === 0) {
    errors.push('Adaptation name is required');
  }
  
  if (!values.category || values.category.trim().length === 0) {
    errors.push('Category is required');
  }
  
  if (!values.description || values.description.trim().length === 0) {
    errors.push('Description is required');
  }
  
  if (!values.scientificParameters || values.scientificParameters.length === 0) {
    errors.push('At least one scientific parameter is required');
  }
  
  return errors;
};

export const formatParametersText = (parameters: string[]): string => {
  return parameters.join('\n');
};

export const parseParametersText = (text: string): string[] => {
  return text.split('\n').filter(p => p.trim().length > 0);
};

export const formatAssociationsText = (associations: string[]): string => {
  return associations.join(', ');
};

export const parseAssociationsText = (text: string): string[] => {
  return text.split(',').map(a => a.trim()).filter(a => a.length > 0);
};

export const getAdaptationsByCategory = (adaptations: Adaptation[]) => {
  return adaptations.reduce((acc, adaptation) => {
    const category = adaptation.category || 'Uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(adaptation);
    return acc;
  }, {} as { [key: string]: Adaptation[] });
};

export const searchAdaptations = (adaptations: Adaptation[], searchTerm: string): Adaptation[] => {
  if (!searchTerm.trim()) return adaptations;
  
  const term = searchTerm.toLowerCase();
  return adaptations.filter(adaptation => 
    adaptation.name.toLowerCase().includes(term) ||
    adaptation.description.toLowerCase().includes(term) ||
    adaptation.category.toLowerCase().includes(term) ||
    adaptation.scientificParameters.some(param => param.toLowerCase().includes(term)) ||
    adaptation.exerciseAssociations.some(assoc => assoc.toLowerCase().includes(term))
  );
}; 